<template>
    <div class="zsh-header-wrapper">
        <v-app-bar
            v-show="visible"
            ref="appBar"
            flat
            class="zsh-header"
            :height="isMobile ? 64 : 90"
            :color="defaultStates.HEADER_BAR_THEME_DARK ? null : 'white'"
            :theme="defaultStates.HEADER_BAR_THEME_DARK ? 'dark' : 'light'"
        >
            <div class="buttons-wrapper">
                <div class="logo-with-buttons">
                    <nuxt-link to="/" class="logo-wrapper">
                        <img :src="logoUrl" alt="ZSH Logo" class="logo" />
                    </nuxt-link>
                </div>
                <div v-if="!isMobile" class="right-buttons">
                    <account-menu />
                </div>
                <v-btn v-if="isMobile" icon class="mobile-header-dropdown" @click="drawer = !drawer">
                    <v-icon>mdi-menu</v-icon>
                </v-btn>
            </div>
        </v-app-bar>

        <!-- Mobile Navigation Drawer -->
        <v-navigation-drawer v-model="drawer" temporary location="right" width="280">
            <v-list>
                <v-list-item class="pa-4">
                    <template v-slot:prepend>
                        <img :src="logoUrl" alt="ZSH Logo" height="32" />
                    </template>
                    <template v-slot:append>
                        <v-btn icon variant="text" @click="drawer = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                </v-list-item>

                <v-divider />

                <!-- Navigation Items -->
                <v-list-item
                    v-for="(item, i) in navigationItems"
                    :key="i"
                    :to="item.to"
                    :value="item.title"
                    color="primary"
                >
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>

                <v-divider class="my-2" />

                <!-- Language Switcher -->
                <v-list-item>
                    <language-switcher />
                </v-list-item>
            </v-list>
        </v-navigation-drawer>

        <!-- Extension Slot for Alerts -->
        <div v-if="extension">
            <div v-if="isMobile" id="under-header" class="w-100" />
            <slot name="alerts" />
        </div>
    </div>
</template>

<script lang="ts">
    import {Component} from 'vue-facing-decorator'
    import {HeaderBar as _HeaderBar} from '@/components/HeaderBar.vue'
    import {authStore} from '@/composables/store-accessor'

    @Component
    export class HeaderBar extends _HeaderBar {
        get logoUrl() {
            // Use computed property to ensure reactivity
            // This prevents the logo from disappearing during navigation/re-renders
            return this.$filters.srcHost('/logo.svg')
        }

        get navigationItems() {
            const items = [
                {title: this.$t('hotels'), to: {name: 'home'}},
                {title: this.$t('about_us'), to: {name: 'infoPage', params: {link: 'about-us'}}},
                {title: this.$t('contact'), to: {name: 'infoPage', params: {link: 'contact'}}},
            ]

            // Only add Messages menu item for authenticated users
            if (!authStore.isGuest) {
                items.unshift({title: this.$t('page_titles.messages_page_title'), to: {name: 'messages'}})
            }

            return items
        }
    }

    export default HeaderBar
</script>

<style scoped lang="scss">
    .zsh-header-wrapper {
        position: relative;
    }

    .zsh-header {
        background-color: var(--zsh-primary-dark) !important;
        color: var(--zsh-primary-bg-700);
        background-position: -91px 0px !important;
        background-size: 560.533% 113.598% !important;
        background-repeat: no-repeat !important;
        padding: 0;
        display: flex;
        flex-direction: column;
        position: relative;

        :global(.v-toolbar__content) {
            background-color: transparent !important;
            width: 80%;
            align-self: center;

            :global(.v-list) {
                background-color: transparent !important;
                color: white !important;

                :global(.v-list-item-title) {
                    color: white !important;
                }

                :global(.account-menu__item-text) {
                    color: white !important;
                }

                :global(.v-icon) {
                    color: white !important;
                }
            }
        }

        @media (min-width: 1024px) {
            background-color: var(--zsh-primary-dark);
            background-position: -91px 0px !important;
            background-size: 109.479% 119.875% !important;
            background-repeat: no-repeat !important;
            padding: 8px 8px;
        }

        @media (min-width: 1440px) {
            padding: 8px 35px;
        }
    }

    .buttons-wrapper {
        display: flex;
        justify-content: space-between;
        background: var(--zsh-primary-dark);
        padding: 10px;
        width: 100%;

        @media (min-width: 1024px) {
            background: transparent;
            padding: 0;
        }

        .logo-with-buttons {
            display: flex;

            .logo-wrapper {
                display: flex;

                .logo {
                    height: 40px;
                    width: 162px;

                    @media (min-width: 1024px) {
                        height: 73px;
                        width: 290px;
                    }
                }
            }

            .left-buttons {
                margin-left: 15px;
                margin-top: 0;
                display: none;

                @media (min-width: 1024px) {
                    margin-left: 15px;
                    margin-top: 17px;
                    display: flex;
                    align-items: center;
                }

                button:first-child {
                    margin-right: 10px;
                }
            }
        }

        .right-buttons {
            display: none;
            margin-top: 0;

            @media (min-width: 1024px) {
                display: flex;
                align-items: center;
                gap: 10px;
                margin-top: 17px;
            }
        }

        .mobile-header-dropdown {
            top: 10px;
            right: 10px;
            box-shadow: none;
            width: 40px;
            height: 40px;
            color: white;

            @media (min-width: 1024px) {
                display: none;
            }
        }
    }

    .btn-secondary {
        color: white !important;
    }

    // Additional styling for AccountMenu in header
    :deep(.account-menu) {
        .v-list {
            background-color: transparent !important;

            .v-list-item {
                .v-list-item-title {
                    color: white !important;

                    .account-menu__item-text {
                        color: white !important;
                    }
                }

                .v-icon {
                    color: white !important;
                }

                &:hover {
                    background-color: rgba(255, 255, 255, 0.1) !important;
                }
            }
        }

        .v-btn {
            color: white !important;
        }
    }
</style>
